import type { PtiAssetType, PtiChecklistAnswerValue } from "../../api/ptiApi";
import {
  PTI_CHECKLIST_ANSWER_NA,
  PTI_CHECKLIST_SECTIONS,
  PTI_POSITION_KEYS,
} from "./ptiConstants";

export type PtiChecklistQuestionType = "YesNo" | "YesNoNa" | "Date" | "Text" | "PassFail";

export type PtiChecklistScalarValue = boolean | "na" | string;

export type { PtiChecklistAnswerValue };

export type PtiChecklistAnswers = Record<string, PtiChecklistAnswerValue>;

export interface PtiChecklistQuestion {
  key: string;
  label: string;
  assetType: PtiAssetType;
  questionType: PtiChecklistQuestionType;
  allowsNote?: boolean;
  section?: string;
  /** true = Yes default, false = No default, undefined = no default */
  defaultValue?: boolean;
}

export interface PtiDamageZone {
  zoneKey: string;
  zoneNumber: number;
  label: string;
  positionKey: string;
}

const vehicleYesNo = (
  key: string,
  label: string,
  section: string,
  defaultValue: boolean
): PtiChecklistQuestion => ({
  key,
  label,
  assetType: "Vehicle",
  questionType: "YesNo",
  section,
  defaultValue,
});

const vehiclePassFail = (
  key: string,
  label: string,
  section: string
): PtiChecklistQuestion => ({
  key,
  label,
  assetType: "Vehicle",
  questionType: "PassFail",
  section,
});

const trailerYesNo = (
  key: string,
  label: string,
  section: string,
  defaultValue: boolean,
  allowsNote = false
): PtiChecklistQuestion => ({
  key,
  label,
  assetType: "Trailer",
  questionType: "YesNo",
  section,
  defaultValue,
  allowsNote: allowsNote || undefined,
});

const trailerYesNoNa = (
  key: string,
  label: string,
  section: string,
  defaultValue: boolean
): PtiChecklistQuestion => ({
  key,
  label,
  assetType: "Trailer",
  questionType: "YesNoNa",
  section,
  defaultValue,
});

export const VEHICLE_PTI_CHECKLIST: PtiChecklistQuestion[] = [
  vehicleYesNo("ext_engine_fluid_leaks", "Engine / Fluid Leaks", PTI_CHECKLIST_SECTIONS.ExteriorCheck, false),
  vehicleYesNo("ext_windshield_glass", "Windshield / Glass", PTI_CHECKLIST_SECTIONS.ExteriorCheck, false),
  vehicleYesNo("ext_mirrors", "Mirrors", PTI_CHECKLIST_SECTIONS.ExteriorCheck, false),
  vehicleYesNo("ext_head_lights", "Head Lights", PTI_CHECKLIST_SECTIONS.ExteriorCheck, false),
  vehicleYesNo("ext_bumper_grill", "Bumper / Grill", PTI_CHECKLIST_SECTIONS.ExteriorCheck, false),
  vehicleYesNo("ext_cab_doors_steps", "Cab / Doors / Steps", PTI_CHECKLIST_SECTIONS.ExteriorCheck, false),
  vehicleYesNo("ext_fuel_tanks_straps", "Fuel Tank(s) / Straps", PTI_CHECKLIST_SECTIONS.ExteriorCheck, false),
  vehicleYesNo("ext_def_tank_cap", "DEF Tank / Cap", PTI_CHECKLIST_SECTIONS.ExteriorCheck, false),
  vehicleYesNo("ext_drive_tires_wheels", "Drive Tires / Wheels", PTI_CHECKLIST_SECTIONS.ExteriorCheck, false),
  vehicleYesNo("ext_fifth_wheel", "Fifth Wheel", PTI_CHECKLIST_SECTIONS.ExteriorCheck, false),
  vehicleYesNo("ext_air_lines_electrical", "Air Lines / Electrical", PTI_CHECKLIST_SECTIONS.ExteriorCheck, false),
  vehicleYesNo("ext_glad_hands_connections", "Glad Hands / Connections", PTI_CHECKLIST_SECTIONS.ExteriorCheck, false),
  vehicleYesNo("ext_mud_flaps", "Mud Flaps", PTI_CHECKLIST_SECTIONS.ExteriorCheck, false),
  vehicleYesNo("ext_air_cleaner", "Air Cleaner", PTI_CHECKLIST_SECTIONS.ExteriorCheck, false),
  vehicleYesNo("ext_coolant_level", "Coolant Level", PTI_CHECKLIST_SECTIONS.ExteriorCheck, false),
  vehicleYesNo("ext_engine_oil_level", "Engine Oil Level", PTI_CHECKLIST_SECTIONS.ExteriorCheck, false),
  vehicleYesNo("ext_power_steering_fluid", "Power Steering Fluid", PTI_CHECKLIST_SECTIONS.ExteriorCheck, false),
  vehicleYesNo("ext_radiator_hoses_belts", "Radiator / Hoses / Belts", PTI_CHECKLIST_SECTIONS.ExteriorCheck, false),
  vehicleYesNo("ext_suspension_air_bags", "Suspension / Air Bags", PTI_CHECKLIST_SECTIONS.ExteriorCheck, false),

  vehicleYesNo("brake_service_brakes", "Service Brakes", PTI_CHECKLIST_SECTIONS.BrakeSystemCheck, false),
  vehicleYesNo("brake_parking_brake", "Parking Brake", PTI_CHECKLIST_SECTIONS.BrakeSystemCheck, false),
  vehicleYesNo("brake_air_pressure_buildup", "Air Pressure Build Up (120PSI)", PTI_CHECKLIST_SECTIONS.BrakeSystemCheck, false),
  vehicleYesNo("brake_low_air_warning", "Low Air Warning", PTI_CHECKLIST_SECTIONS.BrakeSystemCheck, false),
  vehicleYesNo("brake_air_leaks", "Air Leaks", PTI_CHECKLIST_SECTIONS.BrakeSystemCheck, false),
  vehicleYesNo("brake_valve_slack_adjusters", "Brake Valve / Slack Adjusters", PTI_CHECKLIST_SECTIONS.BrakeSystemCheck, false),
  vehicleYesNo("brake_drums_rotors", "Brake Drums / Rotors", PTI_CHECKLIST_SECTIONS.BrakeSystemCheck, false),
  vehicleYesNo("brake_hoses_lines", "Brake Hoses / Lines", PTI_CHECKLIST_SECTIONS.BrakeSystemCheck, false),
  vehicleYesNo("brake_abs_warning_light", "ABS Warning Light", PTI_CHECKLIST_SECTIONS.BrakeSystemCheck, false),

  vehicleYesNo("cab_seat_belt", "Seat Belt", PTI_CHECKLIST_SECTIONS.CabInteriorCheck, true),
  vehicleYesNo("cab_horn", "Horn", PTI_CHECKLIST_SECTIONS.CabInteriorCheck, true),
  vehicleYesNo("cab_gauges_warning_lights", "Gauges / Warning Lights", PTI_CHECKLIST_SECTIONS.CabInteriorCheck, true),
  vehicleYesNo("cab_windshield_wipers", "Windshield Wipers", PTI_CHECKLIST_SECTIONS.CabInteriorCheck, true),
  vehicleYesNo("cab_heater_defroster", "Heater / Defroster", PTI_CHECKLIST_SECTIONS.CabInteriorCheck, true),
  vehicleYesNo("cab_mirrors", "Mirrors", PTI_CHECKLIST_SECTIONS.CabInteriorCheck, true),
  vehicleYesNo("cab_emergency_equipment", "Emergency Equipment", PTI_CHECKLIST_SECTIONS.CabInteriorCheck, true),
  vehicleYesNo("cab_fire_extinguisher", "Fire Extinguisher", PTI_CHECKLIST_SECTIONS.CabInteriorCheck, true),
  vehicleYesNo("cab_triangles_flares", "Triangles / Flares", PTI_CHECKLIST_SECTIONS.CabInteriorCheck, true),
  vehicleYesNo("cab_first_aid_kit", "First Aid Kit", PTI_CHECKLIST_SECTIONS.CabInteriorCheck, true),

  vehicleYesNo("doc_registration_cab_card", "Registration / Cab Card", PTI_CHECKLIST_SECTIONS.DocumentsCheck, true),
  vehicleYesNo("doc_insurance", "Insurance", PTI_CHECKLIST_SECTIONS.DocumentsCheck, true),
  vehicleYesNo("doc_ifta_fuel_card", "IFTA / Fuel Card", PTI_CHECKLIST_SECTIONS.DocumentsCheck, true),
  vehicleYesNo("doc_driver_license", "Driver License", PTI_CHECKLIST_SECTIONS.DocumentsCheck, true),
  vehicleYesNo("doc_medical_card", "Medical Card", PTI_CHECKLIST_SECTIONS.DocumentsCheck, true),
  vehicleYesNo("doc_eld_log_book", "ELD / Log Book", PTI_CHECKLIST_SECTIONS.DocumentsCheck, true),

  vehiclePassFail("air_brake_low_air_warning", "Low Air Warning Test", PTI_CHECKLIST_SECTIONS.AirBrakeTest),
  vehiclePassFail("air_brake_service_brake", "Service Brake Test", PTI_CHECKLIST_SECTIONS.AirBrakeTest),
  vehiclePassFail("air_brake_parking_brake", "Parking Brake Test", PTI_CHECKLIST_SECTIONS.AirBrakeTest),
];

export const VEHICLE_DAMAGE_ZONES: PtiDamageZone[] = [
  { positionKey: PTI_POSITION_KEYS.Front, zoneKey: "grille", zoneNumber: 1, label: "Grille / Radiator" },
  { positionKey: PTI_POSITION_KEYS.Front, zoneKey: "windshield", zoneNumber: 2, label: "Windshield" },
  { positionKey: PTI_POSITION_KEYS.Front, zoneKey: "mirrors", zoneNumber: 3, label: "Side Mirrors" },
  { positionKey: PTI_POSITION_KEYS.Front, zoneKey: "headlights", zoneNumber: 4, label: "Headlights" },
  { positionKey: PTI_POSITION_KEYS.Front, zoneKey: "bumper", zoneNumber: 5, label: "Bumper" },

  { positionKey: PTI_POSITION_KEYS.DriverSide, zoneKey: "mirror", zoneNumber: 3, label: "Side Mirror" },
  { positionKey: PTI_POSITION_KEYS.DriverSide, zoneKey: "cab_door_window", zoneNumber: 6, label: "Cab / Door / Window" },
  { positionKey: PTI_POSITION_KEYS.DriverSide, zoneKey: "steps_straps", zoneNumber: 7, label: "Steps / Fuel Tank Straps" },
  { positionKey: PTI_POSITION_KEYS.DriverSide, zoneKey: "fuel_def", zoneNumber: 8, label: "Fuel Tank / DEF Tank" },
  { positionKey: PTI_POSITION_KEYS.DriverSide, zoneKey: "drive_tires_wheels", zoneNumber: 9, label: "Drive Tires / Wheels" },
  { positionKey: PTI_POSITION_KEYS.DriverSide, zoneKey: "fifth_wheel_chassis", zoneNumber: 10, label: "Fifth Wheel / Rear Chassis" },

  { positionKey: PTI_POSITION_KEYS.PassengerSide, zoneKey: "mirror", zoneNumber: 3, label: "Side Mirror" },
  { positionKey: PTI_POSITION_KEYS.PassengerSide, zoneKey: "cab_door_window", zoneNumber: 6, label: "Cab / Door / Window" },
  { positionKey: PTI_POSITION_KEYS.PassengerSide, zoneKey: "steps_straps", zoneNumber: 7, label: "Steps / Fuel Tank Straps" },
  { positionKey: PTI_POSITION_KEYS.PassengerSide, zoneKey: "fuel_def", zoneNumber: 8, label: "Fuel Tank / DEF Tank" },
  { positionKey: PTI_POSITION_KEYS.PassengerSide, zoneKey: "drive_tires_wheels", zoneNumber: 9, label: "Drive Tires / Wheels" },
  { positionKey: PTI_POSITION_KEYS.PassengerSide, zoneKey: "fifth_wheel_chassis", zoneNumber: 10, label: "Fifth Wheel / Rear Chassis" },

  { positionKey: PTI_POSITION_KEYS.Rear, zoneKey: "cab_rear_air_electrical", zoneNumber: 11, label: "Cab Rear / Air & Electrical Lines" },
  { positionKey: PTI_POSITION_KEYS.Rear, zoneKey: "fifth_wheel_area", zoneNumber: 12, label: "Fifth Wheel Area" },
  { positionKey: PTI_POSITION_KEYS.Rear, zoneKey: "mud_flaps_rear_lights", zoneNumber: 13, label: "Mud Flaps / Rear Lights" },

  { positionKey: PTI_POSITION_KEYS.FifthWheel, zoneKey: "fifth_wheel_plate", zoneNumber: 10, label: "Fifth Wheel Plate" },
];

export const TRAILER_PTI_CHECKLIST: PtiChecklistQuestion[] = [
  {
    key: "dot_sticker_expiration",
    label: "DOT Sticker Expiration Date",
    assetType: "Trailer",
    questionType: "Date",
    section: PTI_CHECKLIST_SECTIONS.DocumentsCheck,
  },
  trailerYesNo(
    "dot_form_in_box",
    "DOT Inspection Form and Registration In Box",
    PTI_CHECKLIST_SECTIONS.DocumentsCheck,
    true
  ),
  {
    key: "plate_number",
    label: "Plate Number",
    assetType: "Trailer",
    questionType: "Text",
    section: PTI_CHECKLIST_SECTIONS.DocumentsCheck,
  },

  trailerYesNo(
    "tandem_pins_retract_slide",
    "Trailer Tandems Pins Retract and Slide",
    PTI_CHECKLIST_SECTIONS.ExteriorAndStructure,
    false
  ),
  trailerYesNo("all_lights_working", "All Lights Working", PTI_CHECKLIST_SECTIONS.ExteriorAndStructure, false, true),
  trailerYesNo(
    "holes_in_trailer_or_floor",
    "Holes in Trailer or Floor",
    PTI_CHECKLIST_SECTIONS.ExteriorAndStructure,
    false,
    true
  ),
  trailerYesNo(
    "dot_reflective_tape",
    "DOT Reflective Tape on DOT Bumper, Doors and Sides Intact",
    PTI_CHECKLIST_SECTIONS.ExteriorAndStructure,
    false
  ),
  trailerYesNo(
    "door_hinges_brackets",
    "Door Hinges and Brackets in Good Condition",
    PTI_CHECKLIST_SECTIONS.ExteriorAndStructure,
    false
  ),

  trailerYesNo("tires_good_condition", "Tires in Good Condition", PTI_CHECKLIST_SECTIONS.AirAndTires, false, true),
  trailerYesNoNa(
    "tiremaxx_hoses",
    "Tiremaxx Hoses Connected and Tiremaxx Light Not On",
    PTI_CHECKLIST_SECTIONS.AirAndTires,
    false
  ),
  trailerYesNo("glad_hand_seals", "Glad Hand Seals in Good Condition", PTI_CHECKLIST_SECTIONS.AirAndTires, false, true),
  trailerYesNo(
    "trailer_airlines",
    "Trailer Airlines Not Too Low or Chaffing",
    PTI_CHECKLIST_SECTIONS.AirAndTires,
    false,
    true
  ),
  trailerYesNoNa("spare_tire_secured", "Spare Tire Secured", PTI_CHECKLIST_SECTIONS.AirAndTires, false),
  trailerYesNo("audible_airleaks", "Audible Airleaks", PTI_CHECKLIST_SECTIONS.AirAndTires, false),

  trailerYesNo("trailer_swept_out", "Trailer Sweeped Out", PTI_CHECKLIST_SECTIONS.Interior, true),
];

export function getPtiChecklistQuestions(assetType: PtiAssetType): PtiChecklistQuestion[] {
  return assetType === "Vehicle" ? VEHICLE_PTI_CHECKLIST : TRAILER_PTI_CHECKLIST;
}

export function getDamageZonesForPosition(positionKey: string): PtiDamageZone[] {
  return VEHICLE_DAMAGE_ZONES.filter((z) => z.positionKey === positionKey);
}

export function buildDefaultChecklistAnswers(
  questions: PtiChecklistQuestion[]
): Record<string, { value?: boolean | "na" | string; note?: string }> {
  const draft: Record<string, { value?: boolean | "na" | string; note?: string }> = {};
  for (const q of questions) {
    if (q.defaultValue !== undefined && (q.questionType === "YesNo" || q.questionType === "YesNoNa")) {
      draft[q.key] = { value: q.defaultValue };
    }
  }
  return draft;
}

export function formatChecklistAnswer(
    question: PtiChecklistQuestion,
    answer?: PtiChecklistAnswerValue | null
): string {
    if (answer === undefined || answer === null) {
        return "—";
    }

    if (typeof answer === "string") {
        if (answer === PTI_CHECKLIST_ANSWER_NA) {
            return "N/A";
        }
        if (question.questionType === "Date") {
            const [year, month, day] = answer.split("-").map(Number);
            if (year && month && day) {
                const parsed = new Date(year, month - 1, day);
                if (!Number.isNaN(parsed.getTime())) {
                    return parsed.toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                    });
                }
            }
        }
        return answer;
    }

    if (typeof answer === "boolean") {
        if (question.questionType === "PassFail") {
            return answer ? "Pass" : "Fail";
        }
        return answer ? "Yes" : "No";
    }

    const valueLabel =
        answer.value === PTI_CHECKLIST_ANSWER_NA
            ? "N/A"
            : question.questionType === "PassFail"
              ? answer.value
                ? "Pass"
                : "Fail"
              : answer.value
                ? "Yes"
                : "No";
    if (answer.note?.trim()) {
        return `${valueLabel} — ${answer.note.trim()}`;
    }

    return valueLabel;
}
